import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Topbar } from "../ui/Topbar";
import { useBondScreenInfo } from "../features/bond/useBondScreenInfo";
import { useBondSchedule } from "../features/bond/useBondSchedule";
import { useBondAccrued } from "../features/bond/useBondAccrued";
import { buildLifeline } from "../features/bond/lifelineData";
import { useFavorites } from "../features/screener/useFavorites";
import { AddPositionModal } from "../features/portfolio/AddPositionModal";
import { AddAlertModal } from "../features/alerts/AddAlertModal";
import { Star } from "../ui/Star";
import {
  Shimmer,
  TickerNum,
} from "../ui/loaders";
import { fmtDate, fmtNum, fmtPct, monthsUntil } from "../lib/format";
import { useScrollRestore } from "../lib/useScrollRestore";
import type { ScreenedBond } from "../features/screener/useScreener";
import type { BondSchedule } from "../features/bond/useBondSchedule";
import type { LifelineSummary } from "../features/bond/lifelineData";

const TAX_RATE = 0.13;
const TARGET_NET_YTM = 0.16;

const LIST_LEVEL_RU: Record<number, string> = {
  1: "Первый",
  2: "Второй",
  3: "Третий",
};

export default function BondPage() {
  const { secid = "" } = useParams();
  const screenInfo = useBondScreenInfo(secid);
  const schedule = useBondSchedule(secid);
  const accrued = useBondAccrued(secid);
  const { favorites, toggle } = useFavorites();
  const scrollRef = useScrollRestore<HTMLDivElement>(`bond:${secid}`);
  const [adding, setAdding] = useState(false);
  const [alerting, setAlerting] = useState(false);

  const bond = screenInfo.bond;
  const isFav = favorites.has(secid);
  const summary = schedule.data ? buildLifeline(schedule.data).summary : null;

  const titleText = bond?.secname ?? bond?.short_name ?? secid;
  const issuer = bond?.secname
    ? bond.secname.trim().split(/\s+/).slice(0, 2).join(" ")
    : null;
  const subtitleParts: string[] = [];
  if (bond) {
    subtitleParts.push(bond.currency);
    subtitleParts.push(bond.is_floater ? "плавающий купон" : "фикс. купон");
    if (issuer) subtitleParts.push(issuer);
  }

  return (
    <>
      <Topbar
        crumb={
          <Link to="/" style={{ color: "inherit" }}>← скринер · карточка бумаги</Link>
        }
        title={
          <span
            style={{
              display: "inline-flex",
              gap: 12,
              alignItems: "baseline",
              flexWrap: "wrap",
            }}
          >
            <span>{titleText}</span>
            {bond?.list_level === 3 && <span className="chip">L3</span>}
            <span className="mono soft" style={{ fontSize: 12 }}>{secid}</span>
          </span>
        }
        subtitle={subtitleParts.length > 0 ? subtitleParts.join(" · ") : null}
        right={
          <>
            <Star on={isFav} onToggle={() => toggle(secid)} />
            <button
              type="button"
              className="btn"
              onClick={() => setAlerting(true)}
              disabled={!bond}
              title="создать алерт по цене"
            >
              + алерт
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={() => setAdding(true)}
            >
              + В портфель
            </button>
          </>
        }
      />

      <div className="bond-shell bond-page-dense">
        {schedule.isError && (
          <div className="banner error">
            ошибка расписания: {String(schedule.error)}
          </div>
        )}

        <KpiStrip bond={bond} accrued={accrued.data} schedule={schedule.data} />

        <div className="bond-info-grid">
          <div className="bond-col">
            <ParamsTable bond={bond} schedule={schedule.data} />
            <OffersTable schedule={schedule.data} />
          </div>
          <div className="bond-col">
            <YieldTable bond={bond} summary={summary} />
            <CouponHistoryFill
              bond={bond}
              schedule={schedule.data}
              scrollRef={scrollRef}
            />
          </div>
        </div>
      </div>

      {adding && (
        <AddPositionModal secid={secid} onClose={() => setAdding(false)} />
      )}

      {alerting && bond && (
        <AddAlertModal
          secid={secid}
          secname={bond.secname ?? bond.short_name}
          currentPrice={bond.last_price}
          onClose={() => setAlerting(false)}
        />
      )}
    </>
  );
}

// ── KPI strip ─────────────────────────────────────────────────────────────

function KpiStrip({
  bond,
  accrued,
  schedule,
}: {
  bond: ScreenedBond | undefined;
  accrued: number | null | undefined;
  schedule: BondSchedule | undefined;
}) {
  const months = bond ? monthsUntil(bond.maturity) : null;
  const putMonths = bond?.next_put_offer
    ? monthsUntil(bond.next_put_offer)
    : null;
  const hasOfferFromSched = (schedule?.offers.length ?? 0) > 0;
  const maturitySub =
    bond?.next_put_offer && putMonths != null
      ? `put-оферта ${fmtDate(bond.next_put_offer)} · через ${putMonths.toFixed(1)} мес`
      : hasOfferFromSched
      ? "оферта возможна"
      : "без оферты";

  return (
    <div className="bond-kpi-strip grid g-4" style={{ gap: 10 }}>
      <KpiCard
        label="Net YTM"
        value={bond ? fmtPct(bond.net_ytm) : <TickerNum />}
        sub={`после налога ${(TAX_RATE * 100).toFixed(0)}%`}
        accent
      />
      <KpiCard
        label="Gross IRR"
        value={bond ? fmtPct(bond.gross_ytm) : <TickerNum />}
        sub="до налога"
      />
      <KpiCard
        label="Цена"
        value={
          bond ? `${fmtNum(bond.last_price)} ₽` : <Shimmer width={120} height={24} />
        }
        sub={
          bond
            ? accrued != null
              ? `номинал ${fmtNum(bond.face_value)} ₽ · НКД ${fmtNum(accrued)} ₽`
              : `номинал ${fmtNum(bond.face_value)} ₽`
            : undefined
        }
      />
      <KpiCard
        label="Срок до погашения"
        value={
          bond
            ? months != null
              ? `${months.toFixed(1)} мес`
              : "—"
            : <Shimmer width={140} height={24} />
        }
        sub={bond ? maturitySub : undefined}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  accent = false,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="label">{label}</div>
      <div className={`kpi-num${accent ? " accent" : ""}`}>{value}</div>
      {sub && (
        <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

// ── Параметры выпуска ────────────────────────────────────────────────────

function ParamsTable({
  bond,
  schedule,
}: {
  bond: ScreenedBond | undefined;
  schedule: BondSchedule | undefined;
}) {
  const futureAmorts =
    schedule?.amortizations.filter((a) => a.date > today()) ?? [];
  const partialAmorts = futureAmorts.length > 1; // последняя — погашение
  // schedule.offers уже отфильтрован на бэке: только execution_date >= today().
  const offers = schedule?.offers ?? [];
  const sh = <Shimmer width={120} height={12} />;

  return (
    <Section title="Параметры выпуска">
      <Row
        label="Номинал"
        value={bond ? `${fmtNum(bond.face_value)} ₽` : sh}
      />
      <Row
        label="Дата погашения"
        value={bond ? (bond.maturity ? fmtDate(bond.maturity) : "—") : sh}
      />
      <Row
        label="Уровень листинга"
        value={
          bond
            ? bond.list_level != null
              ? LIST_LEVEL_RU[bond.list_level] ?? String(bond.list_level)
              : "—"
            : sh
        }
      />
      <Row
        label="Купон"
        value={
          bond == null
            ? sh
            : bond.is_floater
            ? "Плавающий"
            : "Фиксированный"
        }
      />
      <Row
        label="Амортизация"
        value={
          schedule == null
            ? sh
            : partialAmorts
            ? `${futureAmorts.length - 1} выплат до погашения`
            : "Нет"
        }
      />
      <Row
        label="Оферты"
        value={
          schedule == null
            ? sh
            : offers.length === 0
            ? "Не предусмотрены"
            : `${offers.length} · ближ. ${fmtDate(offers[0].date)}`
        }
      />
      <Row
        label="Досрочный выкуп эмитентом"
        value={
          schedule == null
            ? sh
            : schedule.early_redemption_possible
            ? "Возможен"
            : "Не предусмотрен"
        }
      />
    </Section>
  );
}

// ── Расчёт доходности ────────────────────────────────────────────────────

function YieldTable({
  bond,
  summary,
}: {
  bond: ScreenedBond | undefined;
  summary: LifelineSummary | null;
}) {
  // future_coupons / face_redemption приходят из бэка — это тот же поток, что
  // лёг в net_ytm/gross_ytm (включая fill для коротких расписаний флоатеров,
  // проекцию неподтверждённых купонов и фильтр ex-coupon по record date).
  // Если их вдруг нет (старый клиент / неполный bond), откатываемся на
  // lifeline-summary — это не совпадёт с IRR один-в-один на флоатерах, но не
  // развалит экран.
  const futureCoupons = bond?.future_coupons ?? summary?.futureCoupons ?? null;
  const faceRedemption =
    bond?.face_redemption ?? summary?.faceRedemption ?? null;
  const purchasePrice = bond?.last_price ?? null;
  const tax = futureCoupons != null ? futureCoupons * TAX_RATE : null;
  const bondLoading = bond == null;
  const sumLoading = bond == null && summary == null;
  const sh = <Shimmer width={110} height={12} />;

  return (
    <Section title="Расчёт доходности (на одну бумагу)">
      <Row
        label="Купонный доход до погашения"
        value={
          sumLoading ? sh : futureCoupons != null ? `+ ${fmtNum(futureCoupons)} ₽` : "—"
        }
      />
      <Row
        label="Возврат номинала"
        value={
          sumLoading ? sh : faceRedemption != null ? `+ ${fmtNum(faceRedemption)} ₽` : "—"
        }
      />
      <Row
        label="Цена покупки"
        value={
          bondLoading ? sh : purchasePrice != null ? `− ${fmtNum(purchasePrice)} ₽` : "—"
        }
      />
      <Row
        label="Налог 13 % на купоны"
        value={sumLoading ? sh : tax != null ? `− ${fmtNum(tax)} ₽` : "—"}
      />
      <Row
        label="Net YTM"
        value={bondLoading ? sh : bond?.net_ytm != null ? fmtPct(bond.net_ytm) : "—"}
        bold
        accent
      />
      <Row
        label="Gross IRR"
        value={bondLoading ? sh : bond?.gross_ytm != null ? fmtPct(bond.gross_ytm) : "—"}
      />
      <Row
        label={`Цена для целевой Net YTM ${(TARGET_NET_YTM * 100).toFixed(0)} %`}
        value={
          bondLoading
            ? sh
            : bond?.target_clean_price != null
            ? `${fmtNum(bond.target_clean_price)} ₽`
            : "—"
        }
      />
    </Section>
  );
}

// ── История купонов · последние 8 ────────────────────────────────────────

// Special «fill» вариант секции «История купонов»: занимает всю оставшуюся
// высоту правой колонки. Заголовок и сводка зафиксированы сверху, скроллится
// только тело таблицы (thead остаётся видимой через position:sticky).
function CouponHistoryFill({
  bond,
  schedule,
  scrollRef,
}: {
  bond: ScreenedBond | undefined;
  schedule: BondSchedule | undefined;
  scrollRef: (el: HTMLDivElement | null) => void;
}) {
  const allCoupons = schedule?.coupons ?? [];
  const past = allCoupons.filter((c) => c.date <= today());
  // Свежие сверху — порядок чтения от «недавно» к «давно».
  const reversed = [...past].reverse();
  const total = allCoupons.length;
  const nextDate = bond?.next_coupon_date ?? null;
  const nextAmount = bond?.next_coupon_amount ?? null;
  // received != null хотя бы у одного купона = у пользователя есть открытая
  // позиция по бумаге → показываем колонку-маркер и сводку «зачислено вам».
  const hasPositionContext = past.some((c) => c.received != null);
  const receipts = schedule?.receipts ?? null;

  return (
    <div className="bond-section-fill">
      <div className="bond-section-label">
        История купонов ({past.length}
        {total > past.length ? ` из ${total}` : ""})
      </div>
      {((receipts && receipts.count > 0) || (nextDate && nextAmount != null)) && (
        <div className="bond-section-summary soft">
          {receipts && receipts.count > 0 && (
            <>
              <span className="ok">зачислено вам:</span>{" "}
              <b className="ink mono">{fmtNum(receipts.total)} ₽</b>
              {" за "}
              {receipts.count} {pluralCoupons(receipts.count)}
              {receipts.since && <> с {fmtDate(receipts.since)}</>}
            </>
          )}
          {nextDate && nextAmount != null && (
            <>
              {receipts && receipts.count > 0 && " · "}
              Следующий{" "}
              <b className="ink mono">{fmtDate(nextDate)}</b> ·{" "}
              <b className="ink mono">{fmtNum(nextAmount)} ₽</b>
            </>
          )}
        </div>
      )}
      <div ref={scrollRef} className="bond-coupons-body">
        <table className="bond-tbl bond-tbl-coupons">
          <thead>
            <tr>
              {hasPositionContext && <th className="recv-col" aria-label="статус" />}
              <th>Дата</th>
              <th className="num">Купон, ₽</th>
              <th className="num">Δ</th>
            </tr>
          </thead>
          <tbody>
            {reversed.length === 0 && (
              <tr>
                <td
                  colSpan={hasPositionContext ? 4 : 3}
                  className="soft"
                >
                  выплат пока не было
                </td>
              </tr>
            )}
            {reversed.map((c, i) => {
              // i+1 «вниз» в reversed = предыдущий по времени.
              const earlier = reversed[i + 1]?.amount ?? null;
              const delta =
                earlier != null && c.amount != null
                  ? c.amount - earlier
                  : null;
              const cls =
                delta == null
                  ? ""
                  : delta > 0
                  ? "ok"
                  : delta < 0
                  ? "bad"
                  : "";
              const recvCls = c.received === true ? "recv-row" : "";
              return (
                <tr key={c.date} className={recvCls}>
                  {hasPositionContext && (
                    <td className="recv-col">
                      {c.received === true && (
                        <span
                          className="recv-dot"
                          title="зачислено по trades"
                        />
                      )}
                    </td>
                  )}
                  <td className="mono" style={{ fontSize: 12.5 }}>
                    {fmtDate(c.date)}
                  </td>
                  <td className="num">
                    {c.amount == null ? "—" : fmtNum(c.amount)}
                  </td>
                  <td className={`num ${cls}`}>
                    {delta == null
                      ? "—"
                      : `${delta > 0 ? "+" : ""}${fmtNum(delta, 2)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function pluralCoupons(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "купон";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "купона";
  return "купонов";
}

// ── Оферты ───────────────────────────────────────────────────────────────

type OfferRow = BondSchedule["offers"][number];
type OfferStatus = "today" | "window-open" | "upcoming";

function offerStatus(o: OfferRow, todayIso: string): OfferStatus {
  if (o.date === todayIso) return "today";
  if (
    o.window_start &&
    o.window_end &&
    o.window_start <= todayIso &&
    todayIso <= o.window_end
  ) {
    return "window-open";
  }
  return "upcoming";
}

const OFFER_STATUS_LABEL: Record<OfferStatus, string> = {
  today: "сегодня",
  "window-open": "окно открыто",
  upcoming: "предстоящая",
};

function OffersTable({ schedule }: { schedule: BondSchedule | undefined }) {
  if (schedule == null) return null;
  const offers = schedule.offers;
  if (offers.length === 0) return null;

  const t = today();

  return (
    <div className="bond-section bond-offers">
      <div className="bond-section-label">Оферты · {offers.length}</div>
      <div className="bond-offers-cols">
        <span>Тип · дата</span>
        <span>Цена выкупа</span>
      </div>
      <div className="bond-offers-list">
        {offers.map((o, i) => {
          const status = offerStatus(o, t);
          return (
            <div
              key={`${o.kind}-${o.date}-${i}`}
              className={`bond-offer-row${status === "window-open" ? " is-window-open" : ""}`}
            >
              <div className="bond-offer-row-top">
                <span className="bond-offer-head">
                  <span className={`offer-badge offer-${o.kind}`}>{o.kind}</span>
                  <span className="mono bond-offer-date">{fmtDate(o.date)}</span>
                </span>
                <span className="mono bond-offer-price">
                  {fmtNum(o.price_pct, 2)} %
                </span>
              </div>
              <div className="bond-offer-row-bottom">
                <span
                  className={o.window_start && o.window_end ? "mono" : "mono soft"}
                >
                  {o.window_start && o.window_end
                    ? `окно ${fmtDate(o.window_start)} → ${fmtDate(o.window_end)}`
                    : "без окна заявок"}
                </span>
                <span className={`offer-status offer-status-${status}`}>
                  {OFFER_STATUS_LABEL[status]}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="soft bond-offers-note">
        put — право инвестора предъявить бумагу к выкупу. call — право эмитента
        досрочно выкупить выпуск. Вне дат графика эмитент{" "}
        {schedule.early_redemption_possible ? "" : "не "}
        может выкупать бумагу.
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bond-section">
      <div className="bond-section-label">{title}</div>
      {children}
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="bond-row">
      <span className="soft">{label}</span>
      <span
        className={`mono${bold ? " bond-row-bold" : ""}${accent ? " accent" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
